import IcEdit from '@assets/details-camera-icons/IcEdit';
import { BaseModel } from '@Core';
import { UsePopUpReturnType } from '@hooks/usePopUp';
import { Box, Stack, Theme, Tooltip } from '@mui/material';
import color from '@theme/Colors';
import { CSSProperties } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import { MdClose, MdDone, MdOutlineDelete } from 'react-icons/md';

interface ActionButtonsGenericsProps<T> {
    isEditing: boolean;
    isDisabled?: boolean;
    item: T;
    condition?: boolean;
    popupConfirmDelete?: UsePopUpReturnType;
    onSave?: (id: number) => void;
    onClose?: () => void;
    onUpsert?: (item: T) => void;
    onDelete?: (id: number) => void;
}

export const ActionButtonsGenerics = <T extends BaseModel>({
    isEditing,
    isDisabled,
    item,
    condition,
    onSave,
    onClose,
    onUpsert,
    onDelete,
    popupConfirmDelete,
}: ActionButtonsGenericsProps<T>) => {
    return (
        <Stack width="10%" alignItems="flex-end" lineHeight={isEditing ? '48px' : 0}>
            {isEditing ? (
                <Stack direction="row" flex={1} alignItems="center" justifyContent="end" spacing={1} width="100%">
                    <ActionButton
                        cursor={isDisabled ? 'pointer' : 'not-allowed'}
                        tooltipLabel="Save"
                        icon={<MdDone style={{ width: 16, height: 16 }} />}
                        bgColor={isDisabled ? (theme) => theme.palette.primary.main : '#DDDDDD'}
                        hoverColor={isDisabled ? (theme) => theme.palette.primary.dark : '#DDDDDD'}
                        onClick={() => onSave && onSave(item.Id!)}
                    />
                    <ActionButton
                        tooltipLabel="Close"
                        icon={<MdClose style={{ width: 16, height: 16 }} />}
                        bgColor={(theme) => theme.palette.error.main}
                        hoverColor={(theme) => theme.palette.error.dark}
                        onClick={onClose!}
                    />
                </Stack>
            ) : condition ? (
                <Stack direction="row" flex={1} alignItems="center" justifyContent="end" spacing={1} width="100%">
                    <ActionButton
                        tooltipLabel="Add"
                        icon={<AiOutlinePlus />}
                        bgColor={color.secondary}
                        hoverColor={color.warning}
                        onClick={() => onUpsert && onUpsert(item)}
                    />
                    <ActionButton
                        tooltipLabel="Delete"
                        icon={<MdOutlineDelete style={{ width: 16, height: 16 }} />}
                        bgColor={(theme) => theme.palette.error.main}
                        hoverColor={(theme) => theme.palette.error.dark}
                        onClick={() => {
                            onDelete && onDelete(item.Id!);
                            popupConfirmDelete?.setTrue();
                        }}
                    />
                </Stack>
            ) : (
                <Stack direction="row" flex={1} alignItems="center" justifyContent="end" spacing={1} width="100%">
                    <ActionButton
                        tooltipLabel="Edit"
                        icon={<IcEdit style={{ width: 14, height: 14 }} />}
                        bgColor={color.secondary}
                        hoverColor={color.warning}
                        onClick={() => onUpsert && onUpsert(item)}
                    />
                    <ActionButton
                        tooltipLabel="Delete"
                        icon={<MdOutlineDelete style={{ width: 16, height: 16 }} />}
                        bgColor={(theme) => theme.palette.error.main}
                        hoverColor={(theme) => theme.palette.error.dark}
                        onClick={() => {
                            onDelete && onDelete(item.Id!);
                            popupConfirmDelete?.setTrue();
                        }}
                    />
                </Stack>
            )}
        </Stack>
    );
};

interface ButtonStyleProps {
    bgColor: ((theme: Theme) => string) | CSSProperties['backgroundColor'];
    hoverColor: ((theme: Theme) => string) | CSSProperties['backgroundColor'];
    cursor?: CSSProperties['cursor'];
}

interface ActionButtonProps extends ButtonStyleProps {
    tooltipLabel: string;
    icon: JSX.Element;
    onClick: () => void;
}

export const buttonStyle = ({ bgColor, cursor = 'pointer', hoverColor }: ButtonStyleProps) => ({
    width: 25,
    height: 25,
    background: bgColor,
    borderRadius: 2,
    color: color.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        background: hoverColor,
        cursor: cursor,
        transition: '0.5s ease-in-out',
    },
});

const ActionButton: React.FC<ActionButtonProps> = ({ tooltipLabel, icon, bgColor, hoverColor, cursor, onClick }) => {
    return (
        <Tooltip placement="top" title={tooltipLabel}>
            <Box
                sx={buttonStyle({
                    bgColor: bgColor,
                    hoverColor: hoverColor,
                    cursor: cursor,
                })}
                onClick={onClick}
            >
                {icon}
            </Box>
        </Tooltip>
    );
};
